/**
 * FaceAccessoryModal - Modal for selecting accessory type when clicking on a face
 */
import { Modal, Button, Space, Typography, Card } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { FaceIDType } from '@infrastructure/three/prestigeService';
import { getPrestigeAccessoryCatalog } from '@infrastructure/three/prestigeService';

const { Text, Title } = Typography;

// Face ID to display name mapping
const FACE_NAMES: Record<FaceIDType, string> = {
  East: 'Side 1',
  West: 'Side 2',
  South0: 'Side 3',
  South1: 'Side 3',
  North0: 'Side 4',
  North1: 'Side 4',
};

type FaceAccessoryModalProps = {
  faceId: FaceIDType | null;
  widthValue: number;
  hasAccessory: boolean;
  onClose: () => void;
  onAddAccessory: (partType: number) => void;
  onRemoveAccessory: () => void;
};

export const FaceAccessoryModal = ({
  faceId,
  widthValue,
  hasAccessory,
  onClose,
  onAddAccessory,
  onRemoveAccessory,
}: FaceAccessoryModalProps) => {
  if (!faceId) return null;

  const faceName = FACE_NAMES[faceId] || faceId;
  const prestigeAccessoryCatalog = getPrestigeAccessoryCatalog({ widthValue });

  const handleAddAccessory = (partType: number) => {
    onAddAccessory(partType);
    onClose();
  };

  const handleRemoveAccessory = () => {
    onRemoveAccessory();
    onClose();
  };

  return (
    <Modal
      title={`Add Accessory - ${faceName}`}
      open={!!faceId}
      onCancel={onClose}
      footer={null}
      width={450}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {hasAccessory && (
          <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Text>This face already has an accessory</Text>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={handleRemoveAccessory}
              >
                Remove
              </Button>
            </Space>
          </Card>
        )}

        <Title level={5} style={{ marginBottom: 8 }}>Select Accessory Type</Title>

        {prestigeAccessoryCatalog.map((option) => (
          <Card
            key={option.partType}
            size="small"
            hoverable
            onClick={() => handleAddAccessory(option.partType)}
            style={{ cursor: 'pointer' }}
          >
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space align="start">
                <img
                  src={option.imgSrc}
                  alt={option.name}
                  style={{
                    width: 64,
                    height: 40,
                    objectFit: 'cover',
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <Text strong>{option.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{option.description}</Text>
                </div>
              </Space>
              <PlusOutlined style={{ color: '#1890ff' }} />
            </Space>
          </Card>
        ))}
      </Space>
    </Modal>
  );
};
